import { motion } from "framer-motion";
import { colors, fonts } from "@/lib/theme";

interface HeaderProps {
  title?: string | null;
  description?: string | null;
}

/**
 * Option 5: Elegant Frame Build
 * Frame lines build around the content with corner accents
 */
export function ElegantFrameHeader({ title, description }: HeaderProps) {
  const hasContent = title || description;

  return (
    <div
      className="min-h-screen flex flex-col items-center justify-center p-8 relative overflow-hidden"
      style={{ backgroundColor: "#FAF8F6" }}
    >
      {/* Frame container - tighter margins on mobile */}
      <div className="absolute pointer-events-none top-[5%] left-[5%] right-[5%] bottom-[5%] md:top-[10%] md:left-[10%] md:right-[10%] md:bottom-[10%]">
        {/* Top line */}
        <motion.div
          initial={{ width: 0 }}
          whileInView={{ width: "100%" }}
          viewport={{ once: false, amount: 0.3 }}
          transition={{ duration: 0.8, ease: [0.4, 0, 0.2, 1] }}
          className="absolute top-0 left-0 h-px"
          style={{ backgroundColor: colors.primary, opacity: 0.25 }}
        />

        {/* Bottom line */}
        <motion.div
          initial={{ width: 0 }}
          whileInView={{ width: "100%" }}
          viewport={{ once: false, amount: 0.3 }}
          transition={{ duration: 0.8, ease: [0.4, 0, 0.2, 1], delay: 0.2 }}
          className="absolute bottom-0 left-0 h-px"
          style={{ backgroundColor: colors.primary, opacity: 0.25 }}
        />

        {/* Left line */}
        <motion.div
          initial={{ height: 0 }}
          whileInView={{ height: "100%" }}
          viewport={{ once: false, amount: 0.3 }}
          transition={{ duration: 0.8, ease: [0.4, 0, 0.2, 1], delay: 0.4 }}
          className="absolute top-0 left-0 w-px"
          style={{ backgroundColor: colors.primary, opacity: 0.25 }}
        />

        {/* Right line */}
        <motion.div
          initial={{ height: 0 }}
          whileInView={{ height: "100%" }}
          viewport={{ once: false, amount: 0.3 }}
          transition={{ duration: 0.8, ease: [0.4, 0, 0.2, 1], delay: 0.6 }}
          className="absolute bottom-0 right-0 w-px"
          style={{ backgroundColor: colors.primary, opacity: 0.25 }}
        />

        {/* Corner accents */}
        {[
          { top: -10, left: -10, borderTop: true, borderLeft: true },
          { top: -10, right: -10, borderTop: true, borderRight: true },
          { bottom: -10, left: -10, borderBottom: true, borderLeft: true },
          { bottom: -10, right: -10, borderBottom: true, borderRight: true },
        ].map((corner, i) => (
          <motion.div
            key={i}
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: false, amount: 0.3 }}
            transition={{ duration: 0.5, delay: 1 }}
            className="absolute w-5 h-5"
            style={{
              top: corner.top,
              bottom: corner.bottom,
              left: corner.left,
              right: corner.right,
              borderColor: colors.accent,
              borderTopWidth: corner.borderTop ? 2 : 0,
              borderBottomWidth: corner.borderBottom ? 2 : 0,
              borderLeftWidth: corner.borderLeft ? 2 : 0,
              borderRightWidth: corner.borderRight ? 2 : 0,
            }}
          />
        ))}
      </div>

      {/* Content */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: false, amount: 0.3 }}
        transition={{ duration: 0.8, ease: [0.4, 0, 0.2, 1], delay: 0.8 }}
        className="text-center z-10 max-w-2xl"
      >
        {title && (
          <h2
            className="text-2xl sm:text-3xl md:text-5xl mb-4 md:mb-6"
            style={{
              fontFamily: fonts.serif,
              color: colors.text,
              fontWeight: 400,
              letterSpacing: "0.03em",
            }}
          >
            {title}
          </h2>
        )}

        {description && (
          <p
            className="text-base md:text-lg"
            style={{
              fontFamily: fonts.sans,
              color: colors.textMuted,
              lineHeight: 1.8,
            }}
          >
            {description}
          </p>
        )}

        {!hasContent && (
          <div
            style={{
              width: 60,
              height: 2,
              backgroundColor: colors.primaryMuted,
              margin: "0 auto",
            }}
          />
        )}
      </motion.div>
    </div>
  );
}
